import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { createSharedComposable } from '@vueuse/core'
import {
  type Filters,
  applyTimeParams,
  conversationFiltersState,
  mergeUrlParamsIntoFilters,
  resetFilterParams as resetFilterParamsShared,
} from '@klausapp/state/conversations'
import {
  conversationListState,
  formattedListCount as formattedListCountSelector,
  nextTicket as nextTicketSelector,
  previousTicket as previousTicketSelector,
} from '@klausapp/state/conversation-list'
import { session } from '@/composables/useSession'
import { valtioBridge } from '@/utils/valtioBridge'
import analytics from '@/utils/analytics'
import useTicketRouteParams from '@/composables/useTicketRouteParams'

export type { Filters }
export { defaultFilters, TICKETS_PER_PAGE } from '@klausapp/state/conversations'

// Vue reactivity bridges over the two framework-agnostic state modules — read
// through these, write to the state directly. Router navigation, analytics and
// the URL read stay here (framework edge); only pure state/selectors live in packages/state.
const filtersState = valtioBridge(conversationFiltersState)
const listState = valtioBridge(conversationListState)

export default createSharedComposable(() => {
  const router = useRouter()
  const route = useRoute()
  const { connectionId, conversationId, messageId } = useTicketRouteParams()

  // Seed the workspace once from the app session (the state package defaults it to 0).
  if (!filtersState.value.filterParams.workspace) {
    conversationFiltersState.filterParams.workspace = session.workspace.id
  }

  const routeContext = computed(() => ({
    connectionId: connectionId.value,
    conversationId: conversationId.value,
    messageId: messageId.value,
  }))

  // Some refs are exposed as writable computeds to preserve the existing `.value = x` write API
  // while writing through to the underlying Valtio state. Bridges hand out snapshot(), so reads
  // are read-only copies — for nested mutations write directly to the Valtio proxies.
  const ticketList = computed(() => listState.value.ticketList)

  const ticketListCount = computed({
    get: () => listState.value.ticketListCount,
    set: (value) => {
      conversationListState.ticketListCount = value
    },
  })

  const ticketListLoading = computed({
    get: () => listState.value.ticketListLoading,
    set: (value) => {
      conversationListState.ticketListLoading = value
    },
  })

  const ticketListError = computed({
    get: () => listState.value.ticketListError,
    set: (value) => {
      conversationListState.ticketListError = value
    },
  })

  const searchQuery = computed({
    get: () => listState.value.searchQuery,
    set: (value) => {
      conversationListState.searchQuery = value
    },
  })

  const filterParams = computed(() => filtersState.value.filterParams)

  const formattedListCount = computed(() => formattedListCountSelector(listState.value, session.features))

  const nextTicket = computed(() => nextTicketSelector(listState.value, routeContext.value))
  const previousTicket = computed(() => previousTicketSelector(listState.value, routeContext.value))

  // These read the state, not `filterParams` — that is a snapshot, and both
  // transforms can return their input by identity (mergeUrlParamsIntoFilters
  // early-returns `current` when the URL has no params). Assigning a snapshot
  // back into the state makes its nested fields non-writable, after which every
  // `state.filterParams.x = y` silently no-ops.
  const setTimeParams = (newParams: Filters) => {
    conversationFiltersState.filterParams = applyTimeParams(conversationFiltersState.filterParams, newParams)
  }

  const resetFilterParams = () => {
    resetFilterParamsShared(session.workspace.id)
  }

  const updateFilterParamsFromUrl = () => {
    conversationFiltersState.filterParams = mergeUrlParamsIntoFilters(conversationFiltersState.filterParams)
  }

  const getConversationRoute = ({ params, query }) => {
    const ticketParams = {
      connectionId: params.connectionId,
      conversationId: encodeURIComponent(params.conversationId),
      ...(params.messageId ? { messageId: encodeURIComponent(params.messageId) } : {}),
    }

    return {
      name: params.messageId ? 'conversations.message' : 'conversations',
      params: ticketParams,
      query,
    }
  }

  const navigateToPreviousTicket = (evt: KeyboardEvent | MouseEvent) => {
    if (previousTicket.value) {
      router.push(
        getConversationRoute({
          params: {
            connectionId: previousTicket.value.paymentTokenId,
            conversationId: previousTicket.value.externalId,
            messageId: previousTicket.value.messageId,
          },
          query: route.query,
        }),
      )
      analytics.shortcutUsed('key' in evt ? evt.key : 'click')
    }
  }

  const navigateToNextTicket = (evt: KeyboardEvent | MouseEvent) => {
    if (nextTicket.value) {
      router.push(
        getConversationRoute({
          params: {
            connectionId: nextTicket.value.paymentTokenId,
            conversationId: nextTicket.value.externalId,
            messageId: nextTicket.value.messageId,
          },
          query: route.query,
        }),
      )
      analytics.shortcutUsed('key' in evt ? evt.key : 'click')
    }
  }

  return {
    ticketListLoading,
    ticketListError,
    ticketList,
    ticketListCount,
    formattedListCount,
    searchQuery,
    filterParams,
    nextTicket,
    previousTicket,
    resetFilterParams,
    setTimeParams,
    updateFilterParamsFromUrl,
    getConversationRoute,
    navigateToPreviousTicket,
    navigateToNextTicket,
  }
})
